import { NgClass, DecimalPipe } from "@angular/common";
import { Component, OnInit, Input, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { GoogleMap, MapMarker, MapPolyline } from "@angular/google-maps";
import Moment from "moment";

import { ApiService, } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { mapStyles } from "src/app/mapstyles";
import { DatetimePipe, RuntimePipe, DurationPipe, IfEmptyPipe } from "src/app/pipes";
import { DayPickerComponent } from "src/app/shared/day-picker/day-picker.component";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults, AssemblyConfig } from "src/app/sharedinterfaces";

declare const google: any;

@Component({
	selector: "app-exceedances",
	templateUrl: "./exceedances.component.html",
	styleUrls: ["./exceedances.component.scss"],
	imports: [GoogleMap, MapMarker, MapPolyline, NgClass, FormsModule, DvsChartComponent, DayPickerComponent, DecimalPipe, DatetimePipe, RuntimePipe, DurationPipe, IfEmptyPipe]
})
export class ExceedancesComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	components: APIResults.AssetComponentList;
	@Input()
	operation: APIResults.Operation;

	@ViewChild("googleMap", { static: false })
	googleMap: GoogleMap;

	public exceedances: APIResults.Exceedance[];
	public ackUser = null;
	public ackTime = null;
	public ackChecked = false;
	public countData = null;
	public startDate: any = Moment().format("YYYY-MM-DD");
	public endDate: any = this.startDate;


	public path: google.maps.LatLngLiteral[];
	public pathTimes: number[];
	public bounds: google.maps.LatLngBounds;
	public mapFitted = false;
	public mapInfoPosition: google.maps.LatLngLiteral;
	public mapOptions = {
		styles: mapStyles,
		disableDefaultUI: true,
		zoomControl: false,
		// cameraControl: true,
		fullscreenControl: true,
		gestureHandling: "greedy",
		keyboardShortcuts: false,
	};
	public markerOptions: any;
	public startMarkerOptions = {
		icon: {
			url: "/brand/img/startmap.png",
			scaledSize: {
				width: 16,
				height: 16,
			},
			anchor: {
				x: 8,
				y: 8,
			},
		},
	};
	public stopMarkerOptions = {
		icon: {
			url: "/brand/img/stopmap.png",
			scaledSize: {
				width: 16,
				height: 16,
			},
			anchor: {
				x: 8,
				y: 8,
			},
		},
	};
	public polylineOptions = { strokeColor: this.charting.COLOR_OK };
	public chartOptions: Highcharts.Options;
	public chartErrorMessage: string;
	public graphParams: AssemblyConfig.StateParameter[];
	public regimes = undefined;

	async ngOnInit() {
		let iconName = "asset";
		if (this.components && this.components.alt_asset_icon) {
			iconName = this.components.alt_asset_icon;
		}
		this.markerOptions = {
			icon: {
				url: "/brand/img/" + iconName + "map.png",
				scaledSize: {
					width: 38,
					height: 25,
				},
				anchor: {
					x: 19,
					y: 12,
				},
			},
		};

		// Load the available parameters for this asset
		let params = await this.api.GetParameters(this.asset.asset_id) ?? [];
		params = params.filter(p => p.order > 0);
		// Select defaults
		const sticky = sessionStorage.getItem("fdmParams");
		if (sticky) {
			const stickyParams: number[] = JSON.parse(sticky);
			params = params.map(param => ({ ...param, selected: !!stickyParams.includes(param.id) }));
		} else {
			// No sticky settings, select defaults
			params = params.map(param => ({ ...param, selected: !!param.default }));
		}
		params.sort((a, b) => a.order - b.order);
		this.graphParams = params;

		// Fetch the regimes to use later in the regime CI chart, if needed
		this.regimes = await this.api.GetRegimes(this.asset.asset_id);
		await this.operationChange();
		await this.QueryCounts();
	}

	async operationChange() {
		this.chartOptions = undefined;
		this.bounds = undefined;
		await this.RefreshExceedances(); // Need this first for the chart
		this.refreshChart(); // asynchronous
		let path = null;
		try {
			path = await this.api.GetFlightPath(this.asset.asset_id, this.operation.operation_id);
		} catch (err) {
			console.log("Couldn't load flight path", err);
		}
		if (path?.length > 0) {
			// Convert geo array points to LatLngLiterals
			this.path = path.map(point => ({ lat: point[1], lng: point[2] }));
			this.pathTimes = path.map(point => point[0]);
			const bounds = new google.maps.LatLngBounds();
			for (const point of this.path) {
				bounds.extend(point);
			}
			const ne = bounds.getNorthEast();
			const sw = bounds.getSouthWest();
			if (ne.lat() - sw.lat() < 0.01 && ne.lng() - sw.lng() < 0.01) {
				const extendNE = new google.maps.LatLng(ne.lat() + 0.01, ne.lng() + 0.01);
				const extendSW = new google.maps.LatLng(sw.lat() - 0.01, sw.lng() - 0.01);
				bounds.extend(extendNE);
				bounds.extend(extendSW);
			}
			this.mapFitted = false;
			this.bounds = bounds;
			this.mapLoaded();
		} else {
			this.path = null;
			this.pathTimes = null;
			this.bounds = null;
		}
	}

	mapLoaded() {
		if (this.googleMap && this.bounds && !this.mapFitted) {
			this.googleMap.fitBounds(this.bounds);
			this.mapFitted = true;
		}
	}

	async RefreshExceedances() {
		this.exceedances = undefined;
		const result = await this.api.GetExceedances(this.asset.asset_id, this.operation.operation_id);
		this.exceedances = result.rows;
		this.ackUser = result.ackUser;
		this.ackTime = result.ackTime;
		this.ackChecked = !!result.ackUser;
	}

	async UpdateAcknowledged() {
		const result = await this.api.UpdateOperationAck(this.asset.asset_id, this.operation.operation_id, this.ackChecked);
		if (this.ackChecked) {
			this.ackUser = this.api.user.email;
			this.ackTime = new Date();
			this.operation.exceedance_ack_by = this.api.user.user_id;
		} else {
			this.ackUser = null;
			this.ackTime = null;
			this.operation.exceedance_ack_by = null;
		}
		this.asset.exceedancecount = result.exceedancecount;
		this.asset.severeexceedancecount = result.severeexceedancecount;

		// Recalculate totalhimax
		this.asset.totalhimax = 0;
		this.asset.totalhimax = Math.max(this.asset.totalhimax, this.asset.mdmax);
		this.asset.totalhimax = Math.max(this.asset.totalhimax, (this.asset.exceedancecount > 0) ? AssemblyConfig.HumsHiThreshold.Warning : 0);
		this.asset.totalhimax = Math.max(this.asset.totalhimax, (this.asset.severeexceedancecount > 0) ? AssemblyConfig.HumsHiThreshold.Alarm : 0);
	}

	mapPopup(timestamp) {
		if (!this.pathTimes) return;
		this.mapInfoPosition = null;
		const seconds = timestamp / 1000;
		const mapPointIndex = this.pathTimes.findIndex(item => item === seconds);
		if (mapPointIndex >= 0) {
			this.mapInfoPosition = this.path[mapPointIndex];
		}
	}

	async refreshChart() {
		this.chartOptions = undefined;
		this.chartErrorMessage = undefined;
		const selectedColumns = this.graphParams.filter(item => item.selected).map(item => item.id);
		if (selectedColumns.length === 0) {
			this.chartErrorMessage = "No parameters selected";
			return;
		}
		sessionStorage.setItem("fdmParams", JSON.stringify(selectedColumns));
		try {
			const result = await this.api.GetStates(this.asset.asset_id, this.operation.operation_id, selectedColumns);
			if (result.ts.length > 0) {
				const timestamps = result.ts;
				let units;
				const series: any = result.params.map(col => {
					const joined = timestamps.map((stamp, index) => [stamp * 1000, col.values[index]]);
					if (units && units !== col.units) {
						units = "mixed";
					} else {
						units = col.units;
					}
					return {
						name: col.name,
						zIndex: 1,
						data: joined,
						point: {
							events: {
								mouseOver: (event) => {
									this.mapPopup(event.target.x);
								},
							},
						},
						events: {
							mouseOut: () => {
								this.mapPopup(undefined);
							},
						},
						units: col.units,
					};
				});
				const pointDetails = (point: Highcharts.Point) => {
					const regime_id = result.regime_id[point.index];
					if (this.regimes && this.regimes[regime_id]) {
						return "<br/>Regime: " + this.regimes[regime_id].name;
					}
					return "";
				};
				const opt = this.charting.FlightTimeDefaults("", "", "Time", true, series, timestamps, pointDetails, units, this.fleet ? this.fleet.time_offset_hours : 0);

				const exceedanceBands = this.charting.GetExceedanceBands(this.exceedances);
				(<any>opt.xAxis).plotBands = exceedanceBands;
				// opt.data = { columns: result.rows };
				this.chartOptions = opt;
			} else {
				this.chartErrorMessage = "No state data available for this operation";
				this.chartOptions = null;
			}
		} catch (err) {
			console.log(err);
			this.chartErrorMessage = "Couldn't load operation state data";
		}
	}
	async ExportStates() {
		this.api.ExportStates(this.asset.asset_name + "_" + Moment(this.operation.start_time).format("YYYY-MM-DD HHmmss") + "_STATES.csv", this.asset.asset_id, this.operation.operation_id);
	}

	ClearSelections() {
		for (const param of this.graphParams) {
			param.selected = false;
		}
		this.refreshChart();
	}

	public async QueryCounts() {
		const endDateEnd = Moment(this.endDate).add(1, "days").format("YYYY-MM-DD");
		this.countData = null;
		const result = await this.api.GetOperationTotals(this.asset.asset_id, this.startDate, endDateEnd);
		this.countData = result;
	}

	public async DownloadActiveOperations() {
		const endDateEnd = Moment(this.endDate).add(1, "days").format("YYYY-MM-DD");
		const filename = this.asset.asset_name + "_" + Moment(this.startDate).format("YYYY-MM-DD") + "_" + Moment(this.endDate).format("YYYY-MM-DD") + "_ACTIVEOPERATIONS.csv";
		await this.api.DownloadActiveOperations(this.asset.asset_id, filename, this.startDate, endDateEnd);
	}
}
